import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { GalleryGrid } from '../gallery/GalleryGrid';
import { useGalleryImages } from '../../hooks/useGalleryImages';
import { createFlowSurvey, type FlowSurveyDetail } from '../../services/flowSurveyService';
import { lightSurveysKey, listLightSurveys } from '../../services/lightSurveyService';
import '../lighting/Lighting.css';

export interface NewFlowSurveyFormProps {
  aquariumId: string;
  onCreated: (survey: FlowSurveyDetail) => void;
  onCancel: () => void;
}

function nowClock(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * New flow survey: a name, the date and the photo the readings go on (from the
 * gallery). The newest light map's photo is preselected: on the same photo the
 * two maps compare and share their corals (placed once per photo). Another
 * photo can be chosen. Pumps, readings and corals are added on the survey page.
 */
export function NewFlowSurveyForm({ aquariumId, onCreated, onCancel }: NewFlowSurveyFormProps) {
  const { t } = useTranslation();
  const gallery = useGalleryImages({ aquariumId, limit: 12 });
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(today());
  const [clock, setClock] = useState(nowClock());
  // The newest light map with a photo; its photo is the default until one is chosen by hand.
  const lightSurveys = useQuery({ queryKey: lightSurveysKey(aquariumId), queryFn: () => listLightSurveys(aquariumId), enabled: Boolean(aquariumId) });
  const lightMap = lightSurveys.data?.find((s) => s.imageId) ?? null;
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  const imageId = chosen === undefined ? (lightMap?.imageId ?? null) : chosen;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: () => createFlowSurvey(aquariumId, { title: title.trim(), measured_at: new Date(`${date}T12:00:00`).toISOString(), measured_clock: clock || null, image_id: imageId }),
    onSuccess: onCreated,
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (title.trim() === '') next.title = String(t('flow.new.titleRequired'));
    if (!date) next.date = String(t('flow.new.dateRequired'));
    setErrors(next);
    if (Object.keys(next).length === 0) create.mutate();
  };

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="4">
        <Input id="flow-survey-title" label={t('flow.new.title')} value={title} onChange={(e) => setTitle(e.target.value)} error={errors.title} maxLength={120} fullWidth placeholder={t('flow.new.titlePlaceholder')} />
        <Inline gap="3" align="start">
          <Input id="flow-survey-date" type="date" label={t('flow.new.date')} value={date} onChange={(e) => setDate(e.target.value)} error={errors.date} />
          <Input id="flow-survey-time" type="time" label={t('flow.new.time')} helperText={t('flow.new.timeHint')} value={clock} onChange={(e) => setClock(e.target.value)} />
        </Inline>
        <Stack gap="2">
          <Text weight="medium">{t('flow.new.photo')}</Text>
          <Text color="secondary" size="sm">{t('flow.new.photoHint')}</Text>
          {gallery.error ? (
            <LoadErrorState title={t('flow.new.galleryError')} onRetry={() => gallery.refetch?.()} retryLabel={t('flow.retry')} titleLevel={3} />
          ) : (
            <div className="lighting__photo-picker">
              <GalleryGrid
                images={gallery.images}
                isLoading={gallery.isLoading || gallery.isFetchingNextPage}
                hasNextPage={gallery.hasNextPage}
                onLoadMore={gallery.fetchNextPage}
                onImageClick={() => undefined}
                selectionMode
                selectedIds={new Set(imageId ? [imageId] : [])}
                onToggleSelect={(id) => setChosen(imageId === id ? null : id)}
              />
            </div>
          )}
          {!gallery.isLoading && gallery.images.length === 0 && !gallery.error && <Text color="muted" size="sm">{t('flow.new.noPhotos')}</Text>}
          {lightMap && imageId === lightMap.imageId && (
            <Alert tone="info" compact live="off" data-testid="flow-new-light-photo">{t('flow.new.lightPhoto', { title: lightMap.title })}</Alert>
          )}
        </Stack>
        {create.isError && <Alert>{t('flow.new.error')}</Alert>}
        <Inline gap="2" justify="end" wrap>
          <Button type="button" variant="ghost" onClick={onCancel}>{t('flow.cancel')}</Button>
          <Button type="submit" isLoading={create.isPending}>{t('flow.new.submit')}</Button>
        </Inline>
      </Stack>
    </form>
  );
}

export default NewFlowSurveyForm;
